import { DynamicValue } from "./DynamicValue";
import { formatTitleFirstWord } from "utils/formatTitle";
import { Row } from "../Row";
import { ObjectItem } from "./ObjectItem";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { HelpCircle } from "untitledui-js-base";
import { FC, ReactNode } from "react";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";

export type ValueTransformMap = Record<
  string,
  (value: unknown, row: object) => unknown
>;

interface DynamicKeyValueProps {
  detailsKey: string;
  value: unknown;
  isUnavailableValue?: boolean;
  skipEmptyObjects?: boolean;
  fullDate?: boolean;
  order?: string[];
  filter?: string[];
  rename?: Record<string, string | ((object: object) => string)>;
  parentObject?: null | object;
  parentIdx?: number;
  valueTransform?: ValueTransformMap;
  status?: ReactNode;
  actionElement?: ReactNode;
  fullWidthRow?: boolean;
}

const mapAbbrToFull: Record<string, string> = {
  dob: "Date of birth"
};

const getDetailsKey = (
  detailsKey: string,
  rename: Record<string, string | ((object: object) => string)> | undefined,
  parentObject?: object
) => {
  if (!rename) return detailsKey;

  const key = rename[detailsKey];

  if (!key) return detailsKey;

  if (typeof key === "function") return key(parentObject || {});
  return key;
};

const getTransformedValue = (
  value: unknown,
  detailsKey: string,
  valueTransform: ValueTransformMap | undefined,
  parentObject: object
): unknown => {
  if (!valueTransform) return value;

  const transform = valueTransform[detailsKey];

  if (!transform) return value;

  return transform(value, parentObject);
};

const hasAssetProperty = (value: Record<string, unknown>): boolean => {
  const asset = value.asset as Record<string, unknown> | undefined;
  return !!asset?.url && !!asset?.content_type;
};

const isAssetLikeObject = (
  value: Record<string, unknown>,
  detailsKey: string
): boolean =>
  hasAssetProperty(value) ||
  (detailsKey === "asset" && !!value.url && !!value.content_type);

const isDirectAssetEntry = (
  value: unknown,
  detailsKey: string
): value is Record<string, unknown> =>
  detailsKey === "asset" &&
  typeof value === "object" &&
  value !== null &&
  !!(value as Record<string, unknown>).url &&
  !!(value as Record<string, unknown>).content_type;

const isFilteredKey = (detailsKey: string, filter?: string[]): boolean =>
  !!filter && filter.includes(detailsKey.toLowerCase());

const shouldSkipEntry = (
  value: object,
  detailsKey: string,
  skipEmptyObjects?: boolean,
  filter?: string[]
): boolean =>
  (!!skipEmptyObjects && !Object.keys(value).length) ||
  isFilteredKey(detailsKey, filter);

const getAssetName = (
  parentObject: object | null | undefined,
  parentIdx: number | undefined,
  value: Record<string, unknown>
): string => {
  const docNum = (parentIdx || 0) + 1;
  const verificationType = (parentObject as Record<string, unknown>)
    ?.verification_type as string | undefined;
  if (verificationType) return `${verificationType} #${docNum}`;
  const asset = value.asset as Record<string, string> | undefined;
  return asset?.filename || (value as Record<string, string>).filename;
};

const UnavailableValue = () => {
  const { unit } = useUnitsPropertiesDictionary();
  return (
    <Tooltip placement="bottom">
      <TooltipTrigger as="span" className="flex items-center gap-2">
        <HelpCircle className="size-4 stroke-danger-500" />
        <span className="text-xs font-bold">-</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-[370px] rounded-lg border bg-alpha-800 p-4 text-sm text-white">
        No data available. Application was moved from a {unit} where this data
        was not requested
      </TooltipContent>
    </Tooltip>
  );
};

export const DynamicKeyValue: FC<DynamicKeyValueProps> = ({
  detailsKey: _dk,
  value: _value,
  skipEmptyObjects,
  fullDate,
  order,
  filter,
  rename,
  parentObject,
  valueTransform,
  parentIdx,
  isUnavailableValue,
  status,
  actionElement,
  fullWidthRow
}) => {
  const detailsKey = getDetailsKey(
    _dk,
    rename,
    parentObject || (_value as object)
  );

  const value = getTransformedValue(
    _value,
    _dk,
    valueTransform,
    parentObject || {}
  );

  if (isUnavailableValue)
    return (
      <Row
        fullWidthRow={fullWidthRow}
        className="my-2"
        title={formatTitleFirstWord(mapAbbrToFull[detailsKey] || detailsKey)}
        value={
          <DynamicValue
            detailsKey={detailsKey}
            value={<UnavailableValue />}
            fullDate={fullDate}
          />
        }
      />
    );

  if (
    typeof value === "object" &&
    value !== null &&
    !isAssetLikeObject(value as Record<string, unknown>, detailsKey)
  ) {
    if (Array.isArray(value))
      return (
        <div className="space-y-3">
          {value.map((v, idx) => (
            <ObjectItem
              detailsKey={`${detailsKey} ${idx + 1}`}
              key={detailsKey}
              order={order}
              filter={filter}
              fullWidthRow={fullWidthRow}
              skipEmptyObjects={skipEmptyObjects}
              value={v}
              parentObject={parentObject || value}
              rename={rename}
              valueTransform={valueTransform}
              fullDate={fullDate}
              status={status}
              actionElement={actionElement}
            />
          ))}
        </div>
      );

    if (shouldSkipEntry(value, detailsKey, skipEmptyObjects, filter))
      return null;

    return (
      <ObjectItem
        fullWidthRow={fullWidthRow}
        skipEmptyObjects={skipEmptyObjects}
        detailsKey={detailsKey}
        order={order}
        filter={filter}
        value={value}
        rename={rename}
        parentObject={parentObject || value}
        valueTransform={valueTransform}
        fullDate={fullDate}
        status={status}
        actionElement={actionElement}
      />
    );
  }

  if (isFilteredKey(detailsKey, filter)) return null;

  if (isDirectAssetEntry(value, detailsKey))
    return (
      <DynamicValue
        detailsKey={detailsKey}
        value={value}
        fullDate={fullDate}
        assetName={getAssetName(parentObject, parentIdx, value)}
      />
    );

  const isValueBase64 =
    typeof value === "string" &&
    value.startsWith("data:") &&
    value.includes(";base64,");

  if (
    (typeof value === "object" &&
      value !== null &&
      hasAssetProperty(value as Record<string, unknown>)) ||
    isValueBase64
  )
    return (
      <div className="mt-4">
        <h3 className="border-b border-alpha-40 pb-2 text-sm font-bold">
          {formatTitleFirstWord(mapAbbrToFull[detailsKey] || detailsKey)}
        </h3>
        <div className="py-4">
          <DynamicValue
            detailsKey={detailsKey}
            value={value}
            fullDate={fullDate}
          />
        </div>
      </div>
    );

  return (
    <Row
      fullWidthRow={fullWidthRow}
      title={formatTitleFirstWord(mapAbbrToFull[detailsKey] || detailsKey)}
      value={
        <DynamicValue
          detailsKey={detailsKey}
          value={value}
          fullDate={fullDate}
        />
      }
    />
  );
};
